import { useCallback, useEffect, useState } from 'react';
import {
  type BillingEntitlement,
  type BillingStatus,
  type CancelDeletionResult,
  type CheckoutResult,
  type DataRegionState,
  type DataRegionValue,
  type DeleteRequestResult,
  type EmailGatewayState,
  type HomeMetroResult,
  type PortalResult,
  type PromoRedeemErrorCode,
  type PromoRedeemResult,
  type SubscriptionPlanName,
  type TtsPinResponse,
  type VoiceTuningResponse,
  ApiError,
  cancelAccountDeletion,
  confirmDataRegion,
  createCheckoutSession,
  createPortalSession,
  downloadAccountExport,
  getBillingEntitlement,
  getBillingStatus,
  getDataRegion,
  getEmailGateway,
  getHomeMetro,
  getTtsPin,
  getVoiceTuning,
  redeemPromoCode,
  regenerateEmailGatewayToken,
  requestAccountDeletion,
  setHomeMetro,
} from './api';

/**
 * The Settings tab's data hook — the Throughline analogue of useJobsInbox.ts:
 * a GET-on-mount read of everything the screen renders (billing status +
 * the grant-aware entitlement read + voice tuning + tts pin, all read-only),
 * plus the ACTIONS the screen offers (checkout, portal, export,
 * delete-request, delete-cancel), each tracked as its own small phase
 * machine so one button's in-flight state never blocks or gets confused
 * with another's.
 *
 * NO backend changes: every call here hits an EXISTING route (see api.ts's
 * own per-function doc comments for the exact route+line citations).
 */

export interface LoadState {
  billing: BillingStatus | null;
  /** #45 — GET /api/billing/entitlement, the grant-aware read (founder/trial
   *  grantLabel+endsAt when an active promo grant exists). A SEPARATE field
   *  from `billing` above (never merged into it): getBillingStatus's own
   *  `entitlement` sub-field is the grants-BLIND resolution and stays
   *  byte-for-byte unchanged for existing consumers — see api.ts's
   *  BillingEntitlement doc comment for why these are deliberately two
   *  different server truths. */
  entitlement: BillingEntitlement | null;
  voiceTuning: VoiceTuningResponse | null;
  ttsPin: TtsPinResponse | null;
  /** GAP 1 (data-residency Phase 0 client) — GET /api/account/data-region's
   *  served state tuple, read alongside the other four on mount so the
   *  card renders the real unset/suggested/confirmed state on first paint
   *  rather than a placeholder while a second, separate fetch runs. */
  dataRegion: DataRegionState | null;
  /** GATEWAY-API — GET /api/account/email-gateway's served hasToken/address/
   *  createdAt tuple, read alongside the others on mount (same reasoning as
   *  dataRegion above). `address` is masked (null) on this initial read
   *  whenever a token already exists — only the regenerate action's own
   *  response ever carries a REAL plaintext address (see api.ts's
   *  EmailGatewayState doc comment). */
  emailGateway: EmailGatewayState | null;
  /** HYPER-LOCAL (2026-07-06) — GET /api/profile's homeMetro/homeAdminArea/
   *  homeCountry projection, read alongside the others on mount (same
   *  reasoning as dataRegion above: render the real declared-or-not state on
   *  first paint, no second separate fetch). */
  homeMetro: HomeMetroResult['profile'] | null;
  loading: boolean;
  /** Per-SOURCE errors — kept separate (never collapsed into one shared
   *  `error` field) because billing/entitlement/voiceTuning/ttsPin are read
   *  via Promise.allSettled: any one of the four can fail independently
   *  while the others succeed, and a shared field would misattribute (e.g. a
   *  voice-tuning outage rendering under the Billing card's own "couldn't
   *  load" message) — a real bug caught by live-clicking through this screen
   *  with only ONE of the reads stubbed to succeed. */
  billingError: string | null;
  entitlementError: string | null;
  voiceTuningError: string | null;
  ttsPinError: string | null;
  dataRegionError: string | null;
  emailGatewayError: string | null;
  homeMetroError: string | null;
}

const INITIAL_LOAD_STATE: LoadState = {
  billing: null,
  entitlement: null,
  voiceTuning: null,
  ttsPin: null,
  dataRegion: null,
  emailGateway: null,
  homeMetro: null,
  loading: true,
  billingError: null,
  entitlementError: null,
  voiceTuningError: null,
  ttsPinError: null,
  dataRegionError: null,
  emailGatewayError: null,
  homeMetroError: null,
};

export type CheckoutPhase = 'idle' | 'checking-out' | 'error';

export interface CheckoutState {
  phase: CheckoutPhase;
  plan: SubscriptionPlanName | null;
  result: CheckoutResult | null;
  error: string | null;
}

const INITIAL_CHECKOUT_STATE: CheckoutState = { phase: 'idle', plan: null, result: null, error: null };

export type PortalPhase = 'idle' | 'opening' | 'error';

export interface PortalState {
  phase: PortalPhase;
  error: string | null;
}

export type ExportPhase = 'idle' | 'exporting' | 'done' | 'error';

export interface ExportState {
  phase: ExportPhase;
  error: string | null;
}

export type DeletionPhase = 'idle' | 'requesting' | 'canceling' | 'error';

export interface DeletionState {
  phase: DeletionPhase;
  /** The served REQUEST result — rendered verbatim once a request lands
   *  (the brief: "render the 30-day truth verbatim"). Null before any
   *  request has been made THIS session — a page reload does not currently
   *  re-fetch pending-deletion state from the server (no dedicated GET for
   *  it exists; account.routes.ts only ever serves a fresh request/cancel
   *  RESULT, never a "am I currently pending" read), so this state is
   *  honestly scoped to "what happened in this session," not "the account's
   *  true pending status" — the confirm/cancel round trip below still works
   *  correctly regardless, since delete-cancel is a safe no-op either way. */
  result: DeleteRequestResult | null;
  cancelResult: CancelDeletionResult | null;
  error: string | null;
}

const INITIAL_DELETION_STATE: DeletionState = {
  phase: 'idle',
  result: null,
  cancelResult: null,
  error: null,
};

/** GAP 1 — the confirm action's own small phase machine, same shape as
 *  checkout/portal/export/deletion above: 'confirming' while the POST is
 *  in flight, 'error' on failure. There is no terminal 'done' phase (unlike
 *  ExportPhase) because a successful confirm's result lands straight in
 *  `load.dataRegion` (the SAME served-state field the initial GET populates)
 *  — the card re-renders off that one source of truth rather than a
 *  second, parallel "just confirmed" flag that could drift from it. */
export type DataRegionConfirmPhase = 'idle' | 'confirming' | 'error';

export interface DataRegionConfirmState {
  phase: DataRegionConfirmPhase;
  error: string | null;
}

const INITIAL_DATA_REGION_CONFIRM_STATE: DataRegionConfirmState = { phase: 'idle', error: null };

/** GATEWAY-API — the "regenerate address" action's own small phase machine,
 *  same shape as dataRegionConfirm above: 'regenerating' while the POST is
 *  in flight, then the fresh EmailGatewayState (WITH a real, unmasked
 *  address this one time) lands straight in `load.emailGateway` — the SAME
 *  served-state field the initial GET populates — rather than a second,
 *  parallel field. The card reads `load.emailGateway.address` for the
 *  reveal window and knows it will mask on the NEXT reload, honestly. */
export type EmailGatewayRegenPhase = 'idle' | 'regenerating' | 'error';

export interface EmailGatewayRegenState {
  phase: EmailGatewayRegenPhase;
  error: string | null;
}

const INITIAL_EMAIL_GATEWAY_REGEN_STATE: EmailGatewayRegenState = { phase: 'idle', error: null };

/** HYPER-LOCAL — the "declare my home metro" save action's own small phase
 *  machine, same shape as dataRegionConfirm above: 'saving' while the PUT is
 *  in flight, 'error' on failure. No terminal 'done' phase for the same
 *  reason dataRegionConfirm has none — a successful save lands straight in
 *  `load.homeMetro`, the SAME served-state field the initial GET populates. */
export type HomeMetroSavePhase = 'idle' | 'saving' | 'error';

export interface HomeMetroSaveState {
  phase: HomeMetroSavePhase;
  error: string | null;
}

const INITIAL_HOME_METRO_SAVE_STATE: HomeMetroSaveState = { phase: 'idle', error: null };

/** PROMO-TOKENS — the "Have a code?" redeem action's own small phase machine.
 *  Deliberately carries BOTH a raw `error` message (for a generic display
 *  fallback) AND the server's own `errorCode` (invalid_code/expired_code/
 *  exhausted_code/already_redeemed) — Settings.tsx renders DISTINCT Mira-
 *  voice copy per code, per the mission's own "distinct messages" requirement,
 *  rather than the FE re-deriving its own generic wording from the raw
 *  message string alone. `result` holds the served success shape once
 *  redemption lands — its own `message` field is ALREADY the honest,
 *  ready-to-render Mira-voice sentence (promo.routes.ts's own construction),
 *  never re-paraphrased here. */
export type PromoRedeemPhase = 'idle' | 'redeeming' | 'error';

export interface PromoRedeemState {
  phase: PromoRedeemPhase;
  result: PromoRedeemResult | null;
  errorCode: PromoRedeemErrorCode | null;
  error: string | null;
}

const INITIAL_PROMO_REDEEM_STATE: PromoRedeemState = {
  phase: 'idle',
  result: null,
  errorCode: null,
  error: null,
};

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : String(err);
}

/** Narrow an ApiError's own served body.error to the known
 *  PromoRedeemErrorCode vocabulary — defaults to null (never guesses/
 *  invents a code the server didn't actually send) so a genuinely unexpected
 *  failure shape (a network error, a 500) renders via the generic `error`
 *  message instead of a fabricated specific one. */
function promoErrorCode(err: unknown): PromoRedeemErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const code = (body as { error: unknown }).error;
  return code === 'invalid_code' ||
    code === 'expired_code' ||
    code === 'exhausted_code' ||
    code === 'already_redeemed' ||
    code === 'invalid_input'
    ? code
    : null;
}

export function useSettings() {
  const [load, setLoad] = useState<LoadState>(INITIAL_LOAD_STATE);
  const [checkout, setCheckout] = useState<CheckoutState>(INITIAL_CHECKOUT_STATE);
  const [portal, setPortal] = useState<PortalState>({ phase: 'idle', error: null });
  const [exportState, setExportState] = useState<ExportState>({ phase: 'idle', error: null });
  const [deletion, setDeletion] = useState<DeletionState>(INITIAL_DELETION_STATE);
  const [dataRegionConfirm, setDataRegionConfirm] = useState<DataRegionConfirmState>(
    INITIAL_DATA_REGION_CONFIRM_STATE,
  );
  const [promoRedeem, setPromoRedeem] = useState<PromoRedeemState>(INITIAL_PROMO_REDEEM_STATE);
  const [emailGatewayRegen, setEmailGatewayRegen] = useState<EmailGatewayRegenState>(
    INITIAL_EMAIL_GATEWAY_REGEN_STATE,
  );
  const [homeMetroSave, setHomeMetroSave] = useState<HomeMetroSaveState>(INITIAL_HOME_METRO_SAVE_STATE);

  const refresh = useCallback(async () => {
    setLoad((s) => ({
      ...s,
      loading: true,
      billingError: null,
      entitlementError: null,
      voiceTuningError: null,
      ttsPinError: null,
      dataRegionError: null,
      emailGatewayError: null,
      homeMetroError: null,
    }));
    // Independent reads — a failure in one (e.g. voice tuning) must not
    // blank out the others, so each is caught individually rather than
    // letting Promise.all's fail-fast behavior drop every other read. Each
    // settled result's error is attributed to ITS OWN field below — never
    // collapsed into one shared "load.error" (see LoadState's own doc
    // comment for the real bug that shape caused: a voice-tuning failure
    // rendering under the Billing card).
    const [billing, entitlement, voiceTuning, ttsPin, dataRegion, emailGateway, homeMetro] =
      await Promise.allSettled([
        getBillingStatus(),
        getBillingEntitlement(),
        getVoiceTuning(),
        getTtsPin(),
        getDataRegion(),
        getEmailGateway(),
        getHomeMetro(),
      ]);
    setLoad({
      billing: billing.status === 'fulfilled' ? billing.value : null,
      entitlement: entitlement.status === 'fulfilled' ? entitlement.value : null,
      voiceTuning: voiceTuning.status === 'fulfilled' ? voiceTuning.value : null,
      ttsPin: ttsPin.status === 'fulfilled' ? ttsPin.value : null,
      dataRegion: dataRegion.status === 'fulfilled' ? dataRegion.value : null,
      emailGateway: emailGateway.status === 'fulfilled' ? emailGateway.value : null,
      homeMetro: homeMetro.status === 'fulfilled' ? homeMetro.value.profile : null,
      loading: false,
      billingError: billing.status === 'rejected' ? errorMessage(billing.reason) : null,
      entitlementError: entitlement.status === 'rejected' ? errorMessage(entitlement.reason) : null,
      voiceTuningError: voiceTuning.status === 'rejected' ? errorMessage(voiceTuning.reason) : null,
      ttsPinError: ttsPin.status === 'rejected' ? errorMessage(ttsPin.reason) : null,
      dataRegionError: dataRegion.status === 'rejected' ? errorMessage(dataRegion.reason) : null,
      emailGatewayError: emailGateway.status === 'rejected' ? errorMessage(emailGateway.reason) : null,
      homeMetroError: homeMetro.status === 'rejected' ? errorMessage(homeMetro.reason) : null,
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runCheckout = useCallback(async (plan: SubscriptionPlanName) => {
    setCheckout({ phase: 'checking-out', plan, result: null, error: null });
    try {
      const result = await createCheckoutSession(plan);
      setCheckout({ phase: 'idle', plan, result, error: null });
      // 'configured' is absent entirely on the {soldOut:true} branch of the
      // union (see api.ts's CheckoutResult) — the `in` check narrows to the
      // 3 branches that DO carry it before `.configured`/`.checkoutUrl` is
      // ever read, rather than reading a property TS can't prove exists.
      if ('configured' in result && result.configured === true && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      setCheckout({ phase: 'error', plan, result: null, error: errorMessage(err) });
    }
  }, []);

  const openPortal = useCallback(async () => {
    setPortal({ phase: 'opening', error: null });
    try {
      const result: PortalResult = await createPortalSession();
      if (result.configured === true) {
        window.location.href = result.portalUrl;
        return;
      }
      setPortal({ phase: 'idle', error: null });
    } catch (err) {
      setPortal({ phase: 'error', error: errorMessage(err) });
    }
  }, []);

  const runExport = useCallback(async () => {
    setExportState({ phase: 'exporting', error: null });
    try {
      await downloadAccountExport();
      setExportState({ phase: 'done', error: null });
    } catch (err) {
      setExportState({ phase: 'error', error: errorMessage(err) });
    }
  }, []);

  const runDeleteRequest = useCallback(async () => {
    setDeletion((s) => ({ ...s, phase: 'requesting', error: null }));
    try {
      const result = await requestAccountDeletion();
      setDeletion((s) => ({ ...s, phase: 'idle', result, error: null }));
    } catch (err) {
      setDeletion((s) => ({ ...s, phase: 'error', error: errorMessage(err) }));
    }
  }, []);

  const runCancelDeletion = useCallback(async () => {
    setDeletion((s) => ({ ...s, phase: 'canceling', error: null }));
    try {
      const cancelResult = await cancelAccountDeletion();
      setDeletion({ phase: 'idle', result: null, cancelResult, error: null });
    } catch (err) {
      setDeletion((s) => ({ ...s, phase: 'error', error: errorMessage(err) }));
    }
  }, []);

  /** GAP 1 — POST /api/account/data-region/confirm. The caller (the
   *  Settings card) decides WHAT region to send (its own picker selection,
   *  or the suggestion the user explicitly accepted) — this action never
   *  reads/guesses a region on its own. On success, the server's returned
   *  state tuple REPLACES load.dataRegion directly (not merged field-by-
   *  field), since the confirm response is itself a complete, fresher
   *  DataRegionState than whatever the initial GET served. */
  const confirmRegion = useCallback(async (region: DataRegionValue) => {
    setDataRegionConfirm({ phase: 'confirming', error: null });
    try {
      const result = await confirmDataRegion(region);
      setDataRegionConfirm({ phase: 'idle', error: null });
      setLoad((s) => ({ ...s, dataRegion: result, dataRegionError: null }));
    } catch (err) {
      setDataRegionConfirm({ phase: 'error', error: errorMessage(err) });
    }
  }, []);

  /** PROMO-TOKENS — POST /api/promo/redeem {code}. On success, the served
   *  grant is NOT invented into a synthetic entitlement here — instead this
   *  re-runs the SAME getBillingEntitlement read `refresh()` already performs
   *  (never a second, parallel copy of that fetch), so load.entitlement picks
   *  up the real, server-resolved grantLabel/endsAt via
   *  resolveEntitlementWithGrants exactly the way every other grant already
   *  renders on this screen (Settings.tsx's existing GrantNote component) —
   *  the redeem card and the billing card converge on ONE source of truth
   *  the moment redemption lands. */
  const runRedeemPromoCode = useCallback(
    async (code: string) => {
      setPromoRedeem({ phase: 'redeeming', result: null, errorCode: null, error: null });
      try {
        const result = await redeemPromoCode(code);
        setPromoRedeem({ phase: 'idle', result, errorCode: null, error: null });
        await refresh();
      } catch (err) {
        setPromoRedeem({
          phase: 'error',
          result: null,
          errorCode: promoErrorCode(err),
          error: errorMessage(err),
        });
      }
    },
    [refresh],
  );

  /** Clears a settled redeem result/error back to idle — e.g. so a "try a
   *  different code" tap resets the card's error state without needing a
   *  full screen reload; the input itself is owned by Settings.tsx, not here. */
  const resetPromoRedeem = useCallback(() => {
    setPromoRedeem(INITIAL_PROMO_REDEEM_STATE);
  }, []);

  /** GATEWAY-API — POST /api/account/email-gateway/regenerate. The caller
   *  (Settings.tsx) surfaces the "old address stops working" warning BEFORE
   *  calling this — this action itself has no confirmation step. On success
   *  the fresh EmailGatewayState (carrying the real, unmasked address this
   *  ONE time) REPLACES load.emailGateway directly, mirroring
   *  confirmRegion's own "the response IS the freshest truth" pattern. */
  const runRegenerateEmailGateway = useCallback(async () => {
    setEmailGatewayRegen({ phase: 'regenerating', error: null });
    try {
      const result = await regenerateEmailGatewayToken();
      setEmailGatewayRegen({ phase: 'idle', error: null });
      setLoad((s) => ({ ...s, emailGateway: result, emailGatewayError: null }));
    } catch (err) {
      setEmailGatewayRegen({ phase: 'error', error: errorMessage(err) });
    }
  }, []);

  /** HYPER-LOCAL — PUT /api/profile { homeMetro, homeAdminArea }. The caller
   *  (the Settings card) decides what to send (the typed metro/region, or
   *  null/null to clear a prior declaration) — this action never guesses a
   *  metro on its own. On success, the server's returned profile projection
   *  REPLACES load.homeMetro directly, mirroring confirmRegion's own "the
   *  response IS the freshest truth" pattern exactly. */
  const saveHomeMetro = useCallback(async (args: { homeMetro: string | null; homeAdminArea: string | null }) => {
    setHomeMetroSave({ phase: 'saving', error: null });
    try {
      const result = await setHomeMetro(args);
      setHomeMetroSave({ phase: 'idle', error: null });
      setLoad((s) => ({ ...s, homeMetro: result.profile, homeMetroError: null }));
    } catch (err) {
      setHomeMetroSave({ phase: 'error', error: errorMessage(err) });
    }
  }, []);

  return {
    load,
    refresh,
    checkout,
    runCheckout,
    portal,
    openPortal,
    exportState,
    runExport,
    deletion,
    runDeleteRequest,
    runCancelDeletion,
    dataRegionConfirm,
    confirmRegion,
    promoRedeem,
    runRedeemPromoCode,
    resetPromoRedeem,
    emailGatewayRegen,
    runRegenerateEmailGateway,
    homeMetroSave,
    saveHomeMetro,
  };
}
